import flatten from 'lodash/flatten';
import lowerCase from 'lodash/lowerCase';

import type { HistoryItemType } from '@appTypes';
import type { AdvancementHistoryItem, AdvancementImport } from '@appTypes/esb';
import AdvancementStatusComponent from '@modules/shared/components/AdvancementStatus/AdvancementStatus';
import {
  advancementFileStatuses,
  advancementStatuses,
} from '@modules/shared/constants';
import getAdvancementDescription from '@modules/utils/getAdvancementDescription';
import personNameBuilder from '@modules/utils/personNameBuilder';
import { getTranslation as getFileStatusTranslation } from '@shared/components/AdvancementFileStatus/utils';
import advancementStatus from '@shared/utils/advancementStatus';
import advancementStatusDate from '@shared/utils/advancementStatusDate';
import normalizeAdvancementType from '@shared/utils/normalizeAdvancementType';

import {
  advancementStatusFilters,
  historyItemsTypes,
} from '../common/constants';
import type { RosterScout } from '../duck/types';

const filterableAdvancementStatusDict = {
  [advancementStatuses.STARTED]: advancementStatusFilters.STARTED,
  [advancementStatuses.COMPLETED]: advancementStatusFilters.COMPLETED,
  [advancementStatuses.COUNSELOR_APPROVED]:
    advancementStatusFilters.COUNSELOR_APPROVED,
  [advancementStatuses.AWARDED]: advancementStatusFilters.AWARDED,
  [advancementStatuses.APPROVED]: advancementStatusFilters.APPROVED,
} as const;

export const getFilterableAdvancementStatus = (status: string) =>
  filterableAdvancementStatusDict[
    status as keyof typeof filterableAdvancementStatusDict
  ];

const isPendingItem = (status: string) =>
  status === advancementStatuses.COMPLETED ||
  status === advancementStatuses.COUNSELOR_APPROVED;

export function denormalizeAdvancementHistoryItem(
  historyItem: AdvancementHistoryItem,
) {
  const { recordedInformation, userId } = historyItem;

  return (
    recordedInformation
      // there's a bunch of essentially empty rows that we can filter out
      // simplest way is to check if status is truthy
      .filter(advancement => advancement.status)
      .map(advancement => {
        const { recordedBy, name: advancementName } = advancement;
        const { firstName, lastName } = recordedBy;
        const advancementType = normalizeAdvancementType(
          advancement.advancementType,
        );
        const description = getAdvancementDescription(
          advancementName,
          advancement.advancementType,
        );

        const status = advancementStatus.getStatus(advancement);
        const type = historyItemsTypes.ADVANCEMENT as
          | 'activity'
          | 'advancement';
        const isAdvancementPending = isPendingItem(status);
        const requirements = advancement.requirements?.map(req => {
          const requirementName = [
            req.requirementNumber || req.listNumber,
            req.name,
          ]
            .filter(Boolean)
            .join(' - ');
          const name = `${requirementName}`;
          return {
            ...req,
            description: name,
            requirementName: name,
            date: req.dateCompleted,
          };
        });
        return {
          ...historyItem,
          ...advancement,
          key: `${advancement.advancementType}:${advancement.id}-uid:${userId}`,
          requirements,
          /** It could be advancement or activity */
          type,
          status,
          /** Label like: `{advancementType}: {name}`. Used inside approve modal */
          description,
          advancementType,
          advancementName,
          /** If status is Completed or Counselor Approved */
          isAdvancementPending,
          date: advancementStatusDate.getCompleted(advancement),
          statusLabel: AdvancementStatusComponent.getTranslation(status),
          filterableStatus: getFilterableAdvancementStatus(status),
          recordedInformation: undefined,
          recordedBy: undefined,
          recordedByFirstName: firstName,
          recordedByLastName: lastName,
          recordedByName: personNameBuilder.short(recordedBy),
        };
      })
  );
}

export const mapAdvancementImportDtoToModel = (
  dto: AdvancementImport[],
  adults: RosterScout[],
) => {
  const transformedItems = dto.map(item => {
    const { fileName, totalRecords, totalErrors, processedDate, ...itemProps } =
      item;
    const unnecesaryContentReg = /import_\d+_/;
    const getDateFromName = (fileName: string) => fileName.substring(0, 10);
    const getShortFileName = (fileName: string) =>
      fileName.replace(unnecesaryContentReg, '');
    const status = (item.status || '').toLowerCase();
    const getRecordedBy = (
      importPersonGuid = '',
      adults: RosterScout[] = [],
    ) => {
      if (!importPersonGuid || !adults) return;

      return adults.find(
        ({ personGuid = '' }) =>
          lowerCase(importPersonGuid) === lowerCase(personGuid!),
      );
    };
    const name = getShortFileName(fileName);
    let date = processedDate;
    if (status === advancementFileStatuses.CANCELLED) {
      date = getDateFromName(name);
    } else if (status === advancementFileStatuses.PENDING) {
      const match = fileName.match(/import_\d+_(\d{4}-\d{2}-\d{2})/);
      // 2nd match is the date group
      date = match?.[1] || '';
    }
    const recordedBy = getRecordedBy(item.personGuid, adults);

    const type: HistoryItemType = historyItemsTypes.ADVANCEMENT_FILE;

    const result = {
      ...itemProps,
      name,
      key: `${item.id}-${item.personGuid}-${item.fileName}`,
      date,
      status,
      statusLabel: getFileStatusTranslation(status),
      type,
      filterableStatus: status,
      totalRecords,
      recordsImported: totalRecords - totalErrors,
      recordsSkipped: totalErrors,
      recordedByFirstName: recordedBy?.firstName,
      recordedByLastName: recordedBy?.lastName,
      recordedByName: personNameBuilder.short(recordedBy || {}),
    };
    return result;
  });

  return transformedItems;
};

export type AdvancementImportDto = ReturnType<
  typeof mapAdvancementImportDtoToModel
>[0];

export const mapAdvancementHistoryDtoToModel = (
  dto: AdvancementHistoryItem[],
) =>
  flatten(dto.map(item => denormalizeAdvancementHistoryItem(item))).map(
    row => ({
      ...row,
      type: historyItemsTypes.ADVANCEMENT,
      name: personNameBuilder.short(row),
    }),
  );

export type DenormalizedHistoryItem = ReturnType<
  typeof denormalizeAdvancementHistoryItem
>[0];
